/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Subscription } from "../types/subscription-types";
import { useToast } from "@/shared/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { API_CONFIG } from "@/infrastructure/config/api";
import { useActiveOrgId } from "@/shared/contexts/SuperAdminOrgContext";

interface SSEEvent {
  type: string;
  data: any;
}

interface UseSubscriptionSSEOptions {
  organizationId?: string;
  enabled?: boolean;
  onConnectionEstablished?: () => void;
  onInitialData?: (subscription: Subscription) => void;
  onConsumptionUpdate?: (data: any) => void;
  onLimitWarning?: (data: any) => void;
  onStatusUpdate?: (data: any) => void;
  onError?: (error: Error) => void;
}

export const useSubscriptionSSE = (options: UseSubscriptionSSEOptions = {}) => {
  // A chave de cache tem que ser a MESMA que o `useSubscription` usa para ler
  // (`["subscription", "my", activeOrgId]`, AB#84509). Escrever em
  // `["subscription", "my"]` gravava num lugar que ninguém lê: o evento chegava
  // e a tela seguia com o valor antigo. `invalidateQueries` funcionava por
  // prefixo e escondia metade do sintoma.
  const activeOrgId = useActiveOrgId();
  // Memoizado: sem isto a chave é um array novo a cada render e derruba a
  // memoização do handler que a tem como dependência.
  const subscriptionQueryKey = useMemo(
    () => ["subscription", "my", activeOrgId] as const,
    [activeOrgId],
  );

  const {
    organizationId,
    enabled = true,
    onConnectionEstablished,
    onInitialData,
    onConsumptionUpdate,
    onLimitWarning,
    onStatusUpdate,
    onError,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const hasShownErrorToast = useRef(false);
  const wasConnectedBefore = useRef(false);

  // Rastrear alertas já exibidos para evitar repetição
  const alertsShownRef = useRef<Set<number>>(new Set());

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  const getAuthToken = useCallback(() => {
    return (
      localStorage.getItem("access_token") ??
      localStorage.getItem("accessToken")
    );
  }, []);

  const connect = useCallback(() => {
    if (!organizationId || !enabled) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setError(new Error("No authentication token found"));
      return;
    }

    try {
      // Limpar conexão anterior se existir
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      const apiUrl = API_CONFIG.API_ROOT_URL;

      if (!apiUrl) {
        throw new Error("API URL not configured");
      }

      // EventSource não suporta headers customizados nativamente
      // Passar o token como query parameter para autenticação.
      // A rota de SSE é servida na raiz da api, sem o prefixo `/api`.
      const baseUrl = apiUrl.replace(/\/api\/?$/, "");
      const url = `${baseUrl}/subscriptions/${organizationId}/stream?token=${encodeURIComponent(token)}`;

      const eventSource = new EventSource(url);

      eventSource.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
        hasShownErrorToast.current = false;
        wasConnectedBefore.current = true;
        setLastUpdate(new Date());

        if (onConnectionEstablished) {
          onConnectionEstablished();
        }
      };

      eventSource.onerror = () => {
        setIsConnected(false);

        const errorObj = new Error("SSE connection error");
        setError(errorObj);

        if (onError) {
          onError(errorObj);
        }

        // Implementar reconexão com backoff exponencial
        if (reconnectAttempts.current < maxReconnectAttempts) {
          // Aumentar delay progressivamente: 2s, 4s, 8s, 16s, 30s...
          const delay = Math.min(
            2000 * Math.pow(2, reconnectAttempts.current),
            30000,
          );

          // Cancela o agendamento anterior antes de criar outro: o EventSource
          // pode emitir `error` mais de uma vez antes do timer disparar, e sem
          // isto cada erro deixava um timer órfão — todos chamando connect(),
          // abrindo conexões paralelas que ninguém fecha.
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = undefined;
            reconnectAttempts.current++;
            connect();
          }, delay);
        } else {
          // Mostrar toast de erro apenas uma vez e apenas se já estava conectado antes
          if (!hasShownErrorToast.current && wasConnectedBefore.current) {
            hasShownErrorToast.current = true;
            toast({
              variant: "destructive",
              title: t("subscription.sse.connectionError"),
              description: t("subscription.sse.connectionErrorDescription"),
            });
          }
        }
      };

      // Handler genérico para todos os eventos
      eventSource.onmessage = (event) => {
        try {
          const parsed: SSEEvent = JSON.parse(event.data);
          handleEvent(parsed);
        } catch (error) {
          // Silently ignore parse errors
        }
      };

      eventSourceRef.current = eventSource;
    } catch (error) {
      setError(error as Error);
      onError?.(error as Error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, enabled, getAuthToken, onError, toast, t]);

  const handleEvent = useCallback(
    (event: SSEEvent) => {
      setLastUpdate(new Date());

      switch (event.type) {
        case "connection_established":
          setIsConnected(true);
          onConnectionEstablished?.();
          break;

        case "initial_data":
          // Atualizar cache do React Query com dados iniciais
          if (event.data.subscription) {
            queryClient.setQueryData(
              subscriptionQueryKey,
              event.data.subscription,
            );
            onInitialData?.(event.data.subscription);
          }
          break;

        case "subscription:consumption_updated":
          // Atualizar apenas o consumption na subscription em cache
          queryClient.setQueryData(
            subscriptionQueryKey,
            (old: Subscription | undefined) => {
              if (!old) return old;

              // Calcular percentual de uso para verificar alertas
              const limit = old.subscribed_plan?.credits?.limit || 0;
              const total = event.data.consumption?.total || 0;
              const percentage = limit > 0 ? (total / limit) * 100 : 0;

              // Determinar marco de alerta (80, 90, 97, 100)
              let alertThreshold = 0;
              if (percentage >= 100) alertThreshold = 100;
              else if (percentage >= 97) alertThreshold = 97;
              else if (percentage >= 90) alertThreshold = 90;
              else if (percentage >= 80) alertThreshold = 80;

              // Resetar alertas se o consumo diminuiu significativamente
              if (percentage < 80) {
                alertsShownRef.current.clear();
              }

              // Mostrar alerta apenas uma vez por marco
              if (
                alertThreshold > 0 &&
                !alertsShownRef.current.has(alertThreshold)
              ) {
                alertsShownRef.current.add(alertThreshold);

                let description = "";
                if (alertThreshold === 100) {
                  description = t("subscription.credits.resourcesLimited");
                } else {
                  description = t("subscription.credits.limitWarning", {
                    percentage: alertThreshold,
                  });
                }

                toast({
                  variant: "destructive",
                  title:
                    alertThreshold === 100
                      ? t("subscription.credits.exhausted")
                      : t("subscription.credits.warning"),
                  description,
                });
              }

              return {
                ...old,
                consumption: event.data.consumption,
                status: event.data.status,
              };
            },
          );
          onConsumptionUpdate?.(event.data);
          break;

        case "subscription:limit_warning":
          // Evento de alerta do backend - processar apenas se necessário
          onLimitWarning?.(event.data);
          break;

        case "subscription:updated":
          // Atualizar todo o objeto subscription
          queryClient.invalidateQueries({ queryKey: subscriptionQueryKey });

          // Mostrar notificação de mudança de status se aplicável
          if (event.data.message) {
            toast({
              title: t("subscription.statusChanged"),
              description: event.data.message,
            });
          }
          onStatusUpdate?.(event.data);
          break;

        case "ping":
          // Apenas atualizar timestamp de última atividade
          setLastUpdate(new Date());
          break;

        default:
          // Ignorar eventos desconhecidos
          break;
      }
    },
    [
      queryClient,
      subscriptionQueryKey,
      toast,
      t,
      onConnectionEstablished,
      onInitialData,
      onConsumptionUpdate,
      onLimitWarning,
      onStatusUpdate,
    ],
  );

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }
    setIsConnected(false);
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttempts.current = 0;
    hasShownErrorToast.current = false;
    connect();
  }, [disconnect, connect]);

  // Conectar/desconectar baseado em organizationId e enabled
  useEffect(() => {
    if (organizationId && enabled) {
      connect();
    } else {
      disconnect();
    }

    // Cleanup ao desmontar
    return () => {
      disconnect();
    };
  }, [organizationId, enabled, connect, disconnect]);

  return {
    isConnected,
    lastUpdate,
    error,
    reconnect,
    disconnect,
  };
};
